type AppointmentScoringStatus = 'FAILED' | 'PROCESSING' | 'SKIPPED' | 'SUCCESS'

/** Rubric order of the 12 scored categories (drives checklist row order). */
const SCORING_CATEGORY_ORDER = [
  'introductionAndRapport',
  'otoscopy',
  'hearingTestIntroduction',
  'audiogramPersonalization',
  'audiogramExpectationSetting',
  'acclimationCounseling',
  'appSetupDeferral',
  'basicUseEssentialTakeaways',
  'hearingJourneyCommitmentReview',
  'pdrReview',
  'payment',
  'followUpScheduling',
] as const

type ScoringCategoryKey = typeof SCORING_CATEGORY_ORDER[number]

/** Per-category max points. Sum across all 12 = 23; 21 when payment is not assessable (99). */
const SCORING_CATEGORY_MAX: Record<ScoringCategoryKey, number> = {
  acclimationCounseling: 2,
  appSetupDeferral: 1,
  audiogramExpectationSetting: 1,
  audiogramPersonalization: 1,
  basicUseEssentialTakeaways: 3,
  followUpScheduling: 1,
  hearingJourneyCommitmentReview: 3,
  hearingTestIntroduction: 1,
  introductionAndRapport: 4,
  otoscopy: 2,
  payment: 2,
  pdrReview: 2,
}

/** Sentinel: only valid for `payment` — "cannot be assessed from transcript". */
const PAYMENT_NOT_ASSESSABLE = 99

/**
 * Record from `GET /api/v1/appointment-scorings`, **after** `stripScoringPii`.
 *
 * ⚠️ PII: the wire `categories` object also carries `summary`, `customerName`,
 * `providerName` (all dropped here) and a free-text `explanation` per category.
 * The `explanation` is intentionally **retained** as `explanations` — it is surfaced in the
 * category-detail popover (any scored transcript category with an explanation, not only
 * deviations) and can echo names from the transcript. It must never
 * be logged or sent to analytics. `stripScoringPii` still drops the other PII fields and
 * keeps a strict allowlist. The authoritative fix for omitting it to score-only consumers
 * remains server-side in ta.summary-service (follow-up).
 */
type AppointmentScoringRecord = {
  appointmentGid: string | null
  categories: Partial<Record<ScoringCategoryKey, number>> | null
  createdAt: string | null
  explanations: Partial<Record<ScoringCategoryKey, string>> | null
  maxTotalScore: number | null
  status: AppointmentScoringStatus
  totalScore: number | null
}

export { PAYMENT_NOT_ASSESSABLE, SCORING_CATEGORY_MAX, SCORING_CATEGORY_ORDER }
export type { AppointmentScoringRecord, AppointmentScoringStatus, ScoringCategoryKey }
